import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { TpETLComponentTypeManifest } from '../interfaces';
import { TpETLPipelineComponent, TpETLDryRun, TpETLDryRunComponentResult } from '../../interfaces';
import { TpETLObjectTypes } from '../../etl-data-types/interfaces';
import {
  createConfigParamTypeExistingColumn,
  createConfigParamTypeExistingColumnList,
  createConfigParamTypeMultipleChoice,
  ETLDryRunObjectTableAddColumn,
} from '../utils';
import {
  addDryRunObjectTable,
  getETLComponentInputObjectTable,
  getETLTableObjectColumn,
  getETLTableObjectColumnOptional,
} from '../../utils';

import { addETLDryRunError } from '../../etlDryRun';

function performDryRun(dryRun: TpETLDryRun, comp: TpETLPipelineComponent): TpETLDryRunComponentResult {
  const inputTable1Obj = getETLComponentInputObjectTable(dryRun, comp, 'sourceTable1');
  const inputTable2Obj = getETLComponentInputObjectTable(dryRun, comp, 'sourceTable2');
  const outputTableObj = addDryRunObjectTable(dryRun, comp.outputObjectIds['result'], comp);

  let fail = false;

  if (!comp.params['matchColumn1']) {
    addETLDryRunError(dryRun, `Missing matching column for table1`, comp.id, 'matchColumn1');
    fail = true;
  }
  if (!comp.params['matchColumn2']) {
    addETLDryRunError(dryRun, `Missing matching column for table2`, comp.id, 'matchColumn2');
    fail = true;
  }
  if (fail) return { abort: false };
  const matchCol1 = getETLTableObjectColumn(inputTable1Obj, comp.params['matchColumn1']);
  ETLDryRunObjectTableAddColumn(
    outputTableObj,
    matchCol1.identifier,
    matchCol1.name,
    matchCol1.description,
    matchCol1.dataType,
  );

  for (const col of comp.params['columnsTable1'].filter((col: string) => col !== comp.params['matchColumn1'])) {
    const colInfo = getETLTableObjectColumn(inputTable1Obj, col);
    ETLDryRunObjectTableAddColumn(
      outputTableObj,
      colInfo.identifier,
      colInfo.name,
      colInfo.description,
      colInfo.dataType,
    );
  }

  for (const col of comp.params['columnsTable2']) {
    const colInfo = getETLTableObjectColumn(inputTable2Obj, col);
    if (getETLTableObjectColumnOptional(outputTableObj, colInfo.identifier)) {
      addETLDryRunError(
        dryRun,
        `Column ${colInfo.identifier} already exists in the output table`,
        comp.id,
        'columnsTable2',
      );
    } else {
      ETLDryRunObjectTableAddColumn(
        outputTableObj,
        colInfo.identifier,
        colInfo.name,
        colInfo.description,
        colInfo.dataType,
      );
    }
  }

  return {
    abort: false,
  };
}

export const manifestETLTableJoin: TpETLComponentTypeManifest = {
  id: 'TableJoin',
  name: 'Join Tables',
  description: 'Merges the rows from two tables, based on a common column between them.',
  categoryId: 'table',
  icon: <FontAwesomeIcon icon={['fas', 'table-pivot']} />,
  inputs: [
    {
      type: TpETLObjectTypes.TABLE,
      id: 'sourceTable1',
      name: 'Source Table 1',
      columns: [],
    },
    {
      type: TpETLObjectTypes.TABLE,
      id: 'sourceTable2',
      name: 'Source Table 2',
      columns: [],
    },
  ],
  outputs: [
    {
      id: 'result',
      name: 'Result table',
      type: TpETLObjectTypes.TABLE,
      description: 'Joined table',
      columns: [],
    },
  ],
  params: [
    {
      id: 'matchColumn1',
      name: 'Matching column (table 1)',
      description: 'Column used in table 1 to match rows with the other table.',
      paramType: createConfigParamTypeExistingColumn('sourceTable1'),
    },
    {
      id: 'matchColumn2',
      name: 'Matching column (table 2)',
      description: 'Column used in table 2 to match rows with the other table.',
      paramType: createConfigParamTypeExistingColumn('sourceTable2'),
    },
    {
      id: 'joinType',
      name: 'Type of join',
      description: 'Method used to merge the rows from both tables.',
      paramType: createConfigParamTypeMultipleChoice([
        { id: 'left', name: 'Left' },
        { id: 'inner', name: 'Inner' },
        { id: 'outer', name: 'Outer' },
      ]),
    },
    {
      id: 'columnsTable1',
      name: 'Columns used (table 1)',
      description:
        'Column from the first table used in the joined output table. The matching column will be included even when not selected.',
      paramType: createConfigParamTypeExistingColumnList('sourceTable1'),
    },
    {
      id: 'columnsTable2',
      name: 'Columns used (table 2)',
      description: 'Column from the second table used in the joined output table.',
      paramType: createConfigParamTypeExistingColumnList('sourceTable2'),
    },
  ],
  performDryRun,
  //renderCustomConfigParams,
};
